/**
 * Les adresses que le site public sait rendre.
 *
 * Elles vivent ici, et non dans `src/App.js`, parce que trois lecteurs en ont
 * besoin et qu'aucun ne peut interroger les autres :
 *
 * - le routeur du site, qui déclare ses `<Route>` ;
 * - l'API, qui sert les fichiers du site et doit distinguer une adresse à
 *   router côté navigateur d'une adresse qui n'existe pas (issue #144) ;
 * - le sitemap, qui les énumère pour les moteurs de recherche (issue #145).
 *
 * Une route ajoutée au site sans être ajoutée ici répondra 404 : c'est le
 * sens de la liste, et la raison pour laquelle elle doit rester la seule.
 */

/**
 * Les routes indexables, dans l'ordre où un plan de site les présenterait.
 *
 * `/team` n'y figure volontairement pas : elle est déclarée dans le routeur
 * mais rend la page « introuvable », avec le téléphone mis en avant. Une
 * adresse qui annonce elle-même qu'elle n'existe pas n'a rien à faire dans un
 * plan de site, et son 404 est la bonne réponse.
 */
export const routesDuSite = [
  '/',
  '/accueil',
  '/services',
  '/offres-abonnement',
  '/reserver-en-ligne',
  '/offrir',
  '/mentions-legales',
  '/cgu',
] as const

export type RouteDuSite = (typeof routesDuSite)[number]

/**
 * L'origine publique du site, sans slash final.
 *
 * Elle est écrite ici plutôt que lue dans l'environnement parce que ses deux
 * usages — la balise canonique et le plan de site — s'adressent aux moteurs de
 * recherche et doivent porter l'adresse de production, quel que soit l'endroit
 * où le site a été construit. Un plan de site généré sur un poste de
 * développement et publié tel quel doit désigner `azoya-spa.com`, pas
 * `localhost`.
 *
 * À ne pas confondre avec `SITE_HOST`, la variable qui commande la redirection
 * du `www` vers l'apex côté serveur (issue #140) : celle-là est un interrupteur
 * par environnement, absent en développement. Les deux désignent le même hôte
 * en production, et doivent le rester — `SITE_HOST` vaut l'hôte de cette
 * origine, sans le schéma.
 */
export const siteCanonique = 'https://azoya-spa.com'

/**
 * Les adresses distinctes que rend le site, `/accueil` étant un doublon de `/`.
 *
 * Le routeur monte le même composant sur les deux, et les liens du site
 * pointent vers les deux — le logo de l'en-tête mène à `/accueil`, l'audit
 * SEOptimer a relevé `/accueil` parmi les liens internes. C'est une
 * duplication interne de contenu : le même défaut que `www` contre l'apex
 * (#140), en plus discret parce qu'il ne se voit pas dans la barre d'adresse.
 */
const CANONIQUES: Readonly<Record<string, string>> = {
  '/accueil': '/',
}

function sansSlashFinal(chemin: string): string {
  return chemin.length > 1 && chemin.endsWith('/') ? chemin.slice(0, -1) : chemin
}

/**
 * L'adresse de référence pour un chemin : celle que la balise canonique doit
 * déclarer, et la seule que le plan de site propose.
 */
export function adresseCanonique(chemin: string): string {
  const normalise = sansSlashFinal(chemin)
  return CANONIQUES[normalise] ?? normalise
}

/** L'adresse canonique en URL absolue, telle qu'une balise `canonical` l'attend. */
export function urlCanonique(chemin: string): string {
  const canonique = adresseCanonique(chemin)
  // `/` deviendrait `https://azoya-spa.com/` : le slash est celui de la racine,
  // et l'origine n'en porte pas — la concaténation est directe.
  return `${siteCanonique}${canonique}`
}

/**
 * Ce que le plan de site présente aux moteurs : les routes du site ramenées à
 * leurs canoniques, sans doublon, dans l'ordre de la liste.
 */
export const routesIndexables: readonly string[] = [
  ...new Set(routesDuSite.map((route) => adresseCanonique(route))),
]

/**
 * Le chemin correspond-il à une route du site ?
 *
 * Le slash final est ignoré : `/services/` est acceptée comme `/services`.
 * C'est le comportement d'avant cette liste, et le retirer transformerait en
 * 404 des liens entrants qui fonctionnent aujourd'hui. La duplication d'URL
 * que cette tolérance laisse ouverte est du ressort de la balise canonique
 * (issue #141), pas du code de statut.
 *
 * La casse, elle, n'est pas normalisée : `/Services` reste une adresse
 * inexistante, ce que Google traite déjà comme telle.
 */
export function estUneRouteDuSite(chemin: string): boolean {
  const normalise = chemin.length > 1 && chemin.endsWith('/') ? chemin.slice(0, -1) : chemin
  return (routesDuSite as readonly string[]).includes(normalise)
}
